import { useLazyQuery, useMutation } from "@apollo/client";
import { IonSkeletonText } from "@ionic/react";
import { isSameDay, isToday, startOfMonth } from "date-fns";
import { AnimatePresence } from "framer-motion";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";
import { useLocation } from "react-router";

import ExpandableSection from "@components/animations/ExpandableSection";
import Button from "@components/buttons/Button";
import CircleButton from "@components/buttons/CircleButton";
import BaseErrors from "@components/fields/BaseErrors";
import PhoneInput from "@components/fields/PhoneInput";
import RadioButtonGroup from "@components/fields/RadioButtonGroup";
import ToggleCheckbox from "@components/fields/ToggleCheckbox";
import Form from "@components/forms/Form";
import FormCondition from "@components/forms/FormCondition";
import styles from "@components/schedule/ScheduleSession.module.css";
import SessionRescheduledConfirmation from "@components/schedule/SessionRescheduledConfirmation";
import TimeSelector from "@components/TimeSelector";
import BodyCopy from "@components/typography/BodyCopy";
import ButtonLink from "@components/typography/ButtonLink";
import Heading2 from "@components/typography/Heading2";
import InternalLink from "@components/typography/InternalLink";
import useMyClient from "@hooks/useMyClient";
import useMyExpert from "@hooks/useMyExpert";
import useNow from "@hooks/useNow";
import useSessionRescheduler from "@hooks/useSessionRescheduler";
import { ExpertSelectTypeEnum } from "@typing/Enums";
import {
  AnalyticsEventCreateDocument,
  AnalyticsEventCreateMutation,
  AnalyticsEventCreateMutationVariables,
  ConsultAvailabilityFragment as ConsultAvailabilityType,
  ConsultDurationEnum,
  ConsultFragment,
  ContactMethodEnum,
  GetConsultAvailabilityDaysDocument,
  GetConsultAvailabilityDaysQuery,
  GetConsultAvailabilityDaysQueryVariables,
  GetJourneyWithPlanQuery
} from "@typing/Generated";
import { generateHTMLId } from "@utils/htmlUtils";
import { primaryExpertRole } from "@utils/modelUtils";
import { contactMethodOptions } from "@utils/optionUtils";
import { journeyRoute } from "@utils/routeUtils";
import { browserTimezone, getDaysOfTheWeekStartingWithMonday, getWeeksForCurrentMonth } from "@utils/timeUtils";

enum Steps {
  AVAILABILITY_SELECT = "AVAILABILITY_SELECT",
  BOOKED = "BOOKED",
  DAY_SELECT = "DAY_SELECT",
  DURATION = "DURATION",
  CONTACT_METHOD = "CONTACT_METHOD",
  MONTH_SELECT = "MONTH_SELECT",
  SEARCH_AVAILABILITIES = "SEARCH_AVAILABILITIES"
}

type Props = {
  consult: ConsultFragment;
  handleClose: () => void;
  journey: GetJourneyWithPlanQuery["journey"];
  onSuccess?: (consult: ConsultFragment) => void;
};

const RescheduleSession = ({ consult, handleClose, journey, onSuccess }: Props) => {
  const location = useLocation();
  const now = useNow();
  const startOfThisMonth = startOfMonth(now);
  const intl = useIntl();
  const myClient = useMyClient();
  const myExpert = useMyExpert();
  const buttonsRef = useRef<HTMLDivElement>(null);

  const [currentStartOfMonth, setCurrentStartOfMonth] = useState<Date>(startOfThisMonth);
  const [expertType, setExpertType] = useState(ExpertSelectTypeEnum.PRIMARY);
  const [originalDuration] = useState(consult.durationType);
  const [originalStartTime] = useState(consult.startTime);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [reschedulingAttemptId] = useState<string>(generateHTMLId());

  const [createAnalyticsEvent] = useMutation<AnalyticsEventCreateMutation, AnalyticsEventCreateMutationVariables>(
    AnalyticsEventCreateDocument
  );

  const handleSuccess = useCallback(
    (consult: ConsultFragment) => {
      onSuccess?.(consult);
      if (myClient) {
        createAnalyticsEvent({
          variables: {
            eventData: {
              consult,
              page: location.pathname,
              reschedulingAttemptId,
              step: Steps.BOOKED,
              type: "rescheduleSession"
            }
          }
        });
      }
    },
    [createAnalyticsEvent, location.pathname, myClient, onSuccess, reschedulingAttemptId]
  );

  const {
    baseErrors,
    form,
    onSubmit,
    state: reschedulerState
  } = useSessionRescheduler({ consult, journey, onSuccess: handleSuccess });

  const calendarWeeks = useMemo(
    () => getWeeksForCurrentMonth({ date: currentStartOfMonth, startsOnMonday: true }),
    [currentStartOfMonth]
  );

  const endTime = useMemo(
    () => new Date(currentStartOfMonth.getFullYear(), currentStartOfMonth.getMonth() + 1, 2, 0, 0, 0),
    [currentStartOfMonth]
  );

  const [getAvailabilityDays, getAvailabilityDaysQuery] = useLazyQuery<
    GetConsultAvailabilityDaysQuery,
    GetConsultAvailabilityDaysQueryVariables
  >(GetConsultAvailabilityDaysDocument, {
    notifyOnNetworkStatusChange: true
  });

  const primaryExpert = useMemo(() => primaryExpertRole(journey.expertRoles)?.expert, [journey.expertRoles]);

  const handleDurationSelect = (newDuration: ConsultDurationEnum) => {
    if (reschedulerState.duration !== newDuration) {
      if (myClient) {
        createAnalyticsEvent({
          variables: { eventData: { reschedulingAttemptId, step: Steps.DURATION, type: "rescheduleSession" } }
        });
      }
      reschedulerState.setDuration(newDuration);
      reschedulerState.setAvailabilitySelected(null);
    }
  };

  useEffect(() => {
    if (primaryExpert) {
      getAvailabilityDays({
        variables: {
          duration: reschedulerState.duration,
          endTime,
          journeyId: journey.id,
          nonPrimaryExperts: expertType === ExpertSelectTypeEnum.ANY,
          startTime: currentStartOfMonth
        }
      }).then(response => {
        if (myClient) {
          createAnalyticsEvent({
            variables: {
              eventData: {
                availabilities: {
                  count:
                    response.data?.consultAvailabilityDays.reduce((acc, day) => acc + day.availabilities.length, 0) ??
                    0,
                  hasSameDayAvailability: response.data?.consultAvailabilityDays.some(
                    day => isToday(day.date) && day.availabilities.length > 0
                  )
                },
                page: location.pathname,
                reschedulingAttemptId,
                step: Steps.SEARCH_AVAILABILITIES,
                type: "rescheduleSession"
              }
            }
          });
        }
      });
    }
  }, [
    createAnalyticsEvent,
    currentStartOfMonth,
    endTime,
    expertType,
    getAvailabilityDays,
    journey,
    location.pathname,
    myClient,
    primaryExpert,
    reschedulerState.duration,
    reschedulingAttemptId
  ]);

  const advanceMonth = useCallback(
    (count: number) => {
      const startOfMonth = new Date(
        currentStartOfMonth.getFullYear(),
        currentStartOfMonth.getMonth() + count,
        1,
        0,
        0,
        0
      );

      setCurrentStartOfMonth(startOfMonth);
    },
    [currentStartOfMonth]
  );

  const handlePreviousMonth = useCallback(() => {
    advanceMonth(-1);
    setSelectedDay(null);
    reschedulerState.setAvailabilitySelected(null);
    if (myClient) {
      createAnalyticsEvent({
        variables: {
          eventData: {
            month: currentStartOfMonth.getMonth(),
            page: location.pathname,
            reschedulingAttemptId,
            step: Steps.MONTH_SELECT,
            type: "rescheduleSession"
          }
        }
      });
    }
  }, [
    advanceMonth,
    createAnalyticsEvent,
    currentStartOfMonth,
    location.pathname,
    myClient,
    reschedulerState,
    reschedulingAttemptId
  ]);

  const handleNextMonth = useCallback(() => {
    advanceMonth(1);
    setSelectedDay(null);
    reschedulerState.setAvailabilitySelected(null);
    if (myClient) {
      createAnalyticsEvent({
        variables: {
          eventData: {
            month: currentStartOfMonth.getMonth(),
            page: location.pathname,
            reschedulingAttemptId,
            step: Steps.MONTH_SELECT,
            type: "rescheduleSession"
          }
        }
      });
    }
  }, [
    advanceMonth,
    createAnalyticsEvent,
    currentStartOfMonth,
    location.pathname,
    myClient,
    reschedulerState,
    reschedulingAttemptId
  ]);

  const handleExpertTypeToggle = useCallback(() => {
    setExpertType(expertType === ExpertSelectTypeEnum.ANY ? ExpertSelectTypeEnum.PRIMARY : ExpertSelectTypeEnum.ANY);
    setSelectedDay(null);
    reschedulerState.setAvailabilitySelected(null);
  }, [expertType, reschedulerState]);

  const nextMonthAllowed = useMemo(() => {
    const outOfScopeMonth = new Date(now.getFullYear(), now.getMonth() + 3, 2).getMonth();
    const nextMonth = new Date(currentStartOfMonth.getFullYear(), currentStartOfMonth.getMonth() + 1, 1, 0, 0, 0);

    return nextMonth.getMonth() !== outOfScopeMonth;
  }, [currentStartOfMonth, now]);

  const previousMonthAllowed = useMemo(() => {
    const badMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0);
    const previousMonth = new Date(currentStartOfMonth.getFullYear(), currentStartOfMonth.getMonth() - 1, 1, 0, 0, 0);

    return previousMonth.getMonth() !== badMonth.getMonth();
  }, [currentStartOfMonth, now]);

  const handleSelectedDayChange = (date: Date) => {
    if (myClient) {
      createAnalyticsEvent({
        variables: {
          eventData: {
            date: date.toISOString(),
            page: location.pathname,
            reschedulingAttemptId,
            step: Steps.DAY_SELECT,
            type: "rescheduleSession"
          }
        }
      });
    }
    setSelectedDay(date);
    reschedulerState.setAvailabilitySelected(null);
  };

  const selectedAvailabilityDay = useMemo(
    () =>
      selectedDay &&
      getAvailabilityDaysQuery.data?.consultAvailabilityDays.find(day => isSameDay(selectedDay, day.date)),
    [getAvailabilityDaysQuery.data?.consultAvailabilityDays, selectedDay]
  );

  const availabilities = useMemo(() => {
    const morningDates: ConsultAvailabilityType[] = [];
    const eveningDates: ConsultAvailabilityType[] = [];

    for (const availability of selectedAvailabilityDay?.availabilities ?? []) {
      const hours = availability.startTime.getHours();

      if (hours <= 12) {
        morningDates.push(availability);
      } else if (hours > 12 && hours <= 24) {
        eveningDates.push(availability);
      }
    }

    return { eveningDates, morningDates };
  }, [selectedAvailabilityDay?.availabilities]);

  const handleAvailabilitySelected = (availability: ConsultAvailabilityType) => {
    if (myClient) {
      createAnalyticsEvent({
        variables: {
          eventData: {
            availability,
            page: location.pathname,
            reschedulingAttemptId,
            step: Steps.AVAILABILITY_SELECT,
            type: "rescheduleSession"
          }
        }
      });
    }
    reschedulerState.setAvailabilitySelected(availability);
    buttonsRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const contactMethod = form.watch("contactMethod");

  useEffect(() => {
    if (myClient) {
      createAnalyticsEvent({
        variables: {
          eventData: {
            contactMethod,
            page: location.pathname,
            reschedulingAttemptId,
            step: Steps.CONTACT_METHOD,
            type: "rescheduleSession"
          }
        }
      });
    }
  }, [contactMethod, createAnalyticsEvent, location.pathname, myClient, reschedulingAttemptId]);

  return (
    <>
      {reschedulerState.rescheduledConsult ? (
        <SessionRescheduledConfirmation
          consult={consult}
          handleClose={handleClose}
          journey={journey}
          originalDuration={originalDuration}
          originalStartTime={originalStartTime}
          rescheduledConsult={reschedulerState.rescheduledConsult}
          setAvailabilitySelected={reschedulerState.setAvailabilitySelected}
          setRescheduledConsult={reschedulerState.setRescheduledConsult}
        />
      ) : (
        <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
          <div className={styles.rescheduleContainer}>
            <div className={styles.rescheduleHeader}>
              <Heading2>
                <FormattedMessage id="pages.consults.reschedule.selectNewDateAndTime" />
              </Heading2>
              <div className={styles.close}>
                <CircleButton
                  ariaLabelKey="accessibility.ariaLabels.close"
                  data-test="close-reschedule"
                  icon="xmark"
                  onClick={handleClose}
                />
              </div>
            </div>

            <div className={styles.calendar}>
              <div className={styles.rescheduleMonthSelector}>
                <CircleButton
                  ariaLabelKey="accessibility.ariaLabels.previous"
                  disabled={!previousMonthAllowed}
                  fill="clear"
                  icon="chevronLeft"
                  onClick={handlePreviousMonth}
                />
                <Heading2 color="gray-500">
                  <FormattedDate month="long" timeZone={browserTimezone()} value={currentStartOfMonth} />
                </Heading2>
                <CircleButton
                  ariaLabelKey="accessibility.ariaLabels.next"
                  disabled={!nextMonthAllowed}
                  fill="clear"
                  icon="chevronRight"
                  onClick={handleNextMonth}
                />
              </div>
              <div className={styles.calendarDays}>
                {getDaysOfTheWeekStartingWithMonday().map(weekday => (
                  <div className={styles.calendarDay} key={weekday}>
                    <BodyCopy color="gray-500">{weekday.slice(0, 1).toUpperCase()}</BodyCopy>
                  </div>
                ))}
              </div>
              {Object.keys(calendarWeeks).map(weekIndex => (
                <div className={styles.calendarDays} key={weekIndex}>
                  {calendarWeeks[weekIndex].map(({ date, index }) => {
                    const availabilityDay = getAvailabilityDaysQuery.data?.consultAvailabilityDays.find(
                      availabilityDay => isSameDay(availabilityDay.date, date)
                    );
                    const isEmpty = !availabilityDay || availabilityDay.availabilities.length === 0;

                    const selected = selectedDay ? isSameDay(selectedDay, date) : false;

                    return (
                      <div
                        className={`${styles.calendarDay} ${isToday(date) ? styles.currentDateNumber : ""} ${
                          isEmpty ? styles.disabledNumber : selected ? styles.selectedDay : styles.availableDay
                        }`}
                        data-test={isEmpty ? "empty-day" : "available-day"}
                        key={index}
                        onClick={() => (isEmpty ? {} : handleSelectedDayChange(date))}
                      >
                        {getAvailabilityDaysQuery.data?.consultAvailabilityDays ? (
                          <div className={styles.dateNumber}>
                            <FormattedDate day="numeric" timeZone="utc" value={date} />
                          </div>
                        ) : (
                          <IonSkeletonText animated className={styles.emptyNumber} />
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
            <div className={styles.rescheduleOptionTitle}>
              <BodyCopy bold color="primary">
                <FormattedMessage id="pages.consults.reschedule.duration" />
              </BodyCopy>
            </div>
            <div className={styles.timeOptions}>
              <TimeSelector
                clientRoles={null}
                onSelect={handleDurationSelect}
                selected={reschedulerState.duration === ConsultDurationEnum.THIRTY_MINUTES}
                text={intl.formatMessage({ id: "pages.consults.reschedule.min30" })}
                value={ConsultDurationEnum.THIRTY_MINUTES}
              />
              <TimeSelector
                clientRoles={null}
                onSelect={handleDurationSelect}
                selected={reschedulerState.duration === ConsultDurationEnum.ONE_HOUR}
                text={intl.formatMessage({ id: "pages.consults.reschedule.min60" })}
                value={ConsultDurationEnum.ONE_HOUR}
              />
            </div>
            {myExpert && (
              <div>
                <div className={styles.rescheduleOptionTitle}>
                  <BodyCopy bold color="primary">
                    <FormattedMessage id="forms.consult.contactMethodLabel" />
                  </BodyCopy>
                </div>
                <RadioButtonGroup
                  control={form.control}
                  error={form.formState.errors.contactMethod}
                  fullWidth
                  name="contactMethod"
                  options={contactMethodOptions(intl)}
                />
                <FormCondition
                  condition={contactMethod === ContactMethodEnum.PHONE}
                  fieldName="phoneNumber"
                  form={form}
                >
                  <div className={styles.rescheduleOptionTitle}>
                    <BodyCopy bold color="primary">
                      <FormattedMessage id="forms.consult.expertPhoneNumberLabel" />
                    </BodyCopy>
                  </div>
                  <PhoneInput
                    autofocus
                    control={form.control}
                    error={form.formState.errors.phoneNumber}
                    name="phoneNumber"
                  />
                </FormCondition>
              </div>
            )}
            <AnimatePresence>
              {(availabilities.morningDates.length > 0 || availabilities.eveningDates.length > 0) && (
                <ExpandableSection
                  isOpen={availabilities.morningDates.length > 0 || availabilities.eveningDates.length > 0}
                  key="reschedule-availabilities"
                >
                  <>
                    {availabilities.morningDates.length > 0 && (
                      <>
                        <div className={styles.rescheduleOptionTitle}>
                          <BodyCopy bold color="primary">
                            <FormattedMessage id="pages.consults.reschedule.morning" />
                          </BodyCopy>
                        </div>
                        <div className={styles.timeOptions}>
                          {availabilities.morningDates.map(availability => {
                            const selected = !!(
                              reschedulerState.availabilitySelected &&
                              availability.startTime.getTime() ==
                                reschedulerState.availabilitySelected?.startTime.getTime()
                            );

                            return (
                              <TimeSelector<ConsultAvailabilityType>
                                clientRoles={journey.clientRoles}
                                expert={expertType === ExpertSelectTypeEnum.ANY ? availability.expert : undefined}
                                key={availability.startTime}
                                onSelect={() => handleAvailabilitySelected(availability)}
                                selected={selected}
                                text={intl.formatTime(availability.startTime, { hour: "numeric", minute: "numeric" })}
                                value={availability.startTime}
                              />
                            );
                          })}
                        </div>
                      </>
                    )}
                    {availabilities.eveningDates.length > 0 && (
                      <>
                        <div className={styles.rescheduleOptionTitle}>
                          <BodyCopy bold color="primary">
                            <FormattedMessage id="pages.consults.reschedule.afternoon" />
                          </BodyCopy>
                        </div>
                        <div className={styles.timeOptions}>
                          {availabilities.eveningDates.map(availability => {
                            const selected = !!(
                              reschedulerState.availabilitySelected &&
                              availability.startTime.getTime() ==
                                reschedulerState.availabilitySelected?.startTime.getTime()
                            );

                            return (
                              <TimeSelector<ConsultAvailabilityType>
                                clientRoles={journey.clientRoles}
                                expert={expertType === ExpertSelectTypeEnum.ANY ? availability.expert : undefined}
                                key={availability.startTime}
                                onSelect={() => handleAvailabilitySelected(availability)}
                                selected={selected}
                                text={intl.formatTime(availability.startTime, { hour: "numeric", minute: "numeric" })}
                                value={availability.startTime}
                              />
                            );
                          })}
                        </div>
                      </>
                    )}
                  </>
                </ExpandableSection>
              )}
            </AnimatePresence>
            <div className={styles.rescheduleAnyExpert}>
              <FormattedMessage
                id="pages.consults.reschedule.cantFind"
                values={{
                  link: (
                    <div className={styles.rescheduleExpertToggle}>
                      <ButtonLink onClick={handleExpertTypeToggle}>
                        <FormattedMessage id="pages.consults.reschedule.bookWithAny" />
                      </ButtonLink>
                    </div>
                  )
                }}
              />
              <ToggleCheckbox
                checked={expertType === ExpertSelectTypeEnum.ANY}
                name="expertType"
                onChange={handleExpertTypeToggle}
              />
            </div>
            <div className={styles.rescheduleButtons} ref={buttonsRef}>
              <Button disabled={!reschedulerState.availabilitySelected || form.formState.isSubmitting} type="submit">
                <FormattedMessage id="pages.consults.reschedule.reschedule" />
              </Button>
              <InternalLink
                linkEventCreateVariables={{ journeyId: journey.id }}
                to={journeyRoute({
                  journeyId: journey.id,
                  linkAfterJourney: `/consults/${consult.id}/cancel`,
                  myClient
                })}
              >
                <FormattedMessage id="pages.consults.reschedule.cancel" />
              </InternalLink>
            </div>
            <div>
              <BaseErrors errors={baseErrors} />
            </div>
          </div>
        </Form>
      )}
    </>
  );
};

export default RescheduleSession;
